// Braking distance boards (300/200/100 m) before the big stops, counting down
// to the corner's turn-in, on the outside of the corner like on real circuits.

import type { Track } from './track';
import { barrierOffsetAt, getLayout, type Side } from './geometry';

export const BOARD_DISTANCES = [300, 200, 100] as const;
export type BoardDistance = (typeof BOARD_DISTANCES)[number];

export interface BrakingBoard {
  /** Distance along the lap of the board, metres. */
  s: number;
  distance: BoardDistance;
  side: Side;
  x: number;
  /** Ground height at the board, metres. */
  y: number;
  z: number;
  /** Driving direction at the board; the board faces oncoming cars. */
  heading: number;
}

/** Corners this tight at the end of a long enough run also get boards. */
const SLOW_CORNER_RADIUS = 60;
const MIN_APPROACH = 200;
/** Gap between the track edge and the board, metres. */
const EDGE_GAP = 6;
/** Keep boards at least this far inside the barrier. */
const BARRIER_GAP = 1.5;

export function brakingBoards(track: Track): BrakingBoard[] {
  const geo = getLayout(track);
  const L = geo.length;
  const sAt = (i: number): number => geo.cum[i % geo.count]!;
  const corners = [...geo.corners].sort((a, b) => a.s - b.s);
  const boards: BrakingBoard[] = [];

  corners.forEach((c, k) => {
    const prev = corners[(k - 1 + corners.length) % corners.length]!;
    const turnIn = sAt(c.start);
    const approach = (turnIn - sAt(prev.end) + L) % L;
    if (!c.heavyBraking && !(c.minRadius < SLOW_CORNER_RADIUS && approach >= MIN_APPROACH)) return;

    const side: Side = c.direction === 'right' ? 'left' : 'right';
    const sign = side === 'right' ? 1 : -1;
    for (const d of BOARD_DISTANCES) {
      if (d > approach - 30) continue;
      const s = (turnIn - d + L) % L;
      const f = track.frameAt(s);
      // Right-hand normal of the driving direction is (tz, -tx).
      const nx = f.tz * sign;
      const nz = -f.tx * sign;
      const edge = side === 'right' ? f.widthRight : f.widthLeft;
      let offset = edge + EDGE_GAP;
      const barrier = barrierOffsetAt(track, track.project(f.x + nx * offset, f.z + nz * offset));
      offset = Math.max(edge + 1.5, Math.min(offset, barrier - BARRIER_GAP));
      boards.push({ s, distance: d, side, x: f.x + nx * offset, y: f.y, z: f.z + nz * offset, heading: f.heading });
    }
  });
  return boards;
}
